import {
  useConnectAccountToSpruce,
  useDisconnectAccountFromSpruce,
} from '@/api/mutations/integrations/spruce';
import useGetMyAccountIntegrations from '@/api/queries/me/useGetMyAccountIntegrations';
import { useSelector } from '@/api/state';
import {
  ConnectionControl,
  ConnectionControlBenefitList,
} from '@/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Form,
  Modal,
  RawInput,
  useToastContext,
} from '@payaca/ambrosia';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { getUserRoles } from '../../../../utils/stateAccessors';

const formSchema = z.object({
  apiKey: z.string().min(1),
});
type FormSchema = z.infer<typeof formSchema>;

export const SpruceConnectionControl: FC = () => {
  const [isSettingUpConnection, setIsSettingUpConnection] = useState(false);
  const disconnectAccountFromSpruce = useDisconnectAccountFromSpruce();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetMyAccountIntegrations();
  const hasConnection = integrations.some(
    (accountIntegration) => accountIntegration.service === 'SPRUCE'
  );
  const userRoles = useSelector(getUserRoles);

  const canUpdate = useMemo(() => {
    return userHasRequiredPermission(userRoles, [
      AccountsPermissions.UPDATE_DESIGN_INTEGRATION,
    ]);
  }, [userRoles]);

  return (
    <ConnectionControl
      allowConnectDisconnect={canUpdate && !isLoadingIntegrations}
      connectionName="Spruce"
      isConnected={hasConnection}
      onAddConnection={() => setIsSettingUpConnection(true)}
      onRemoveConnection={() => disconnectAccountFromSpruce.mutateAsync({})}
    >
      {hasConnection ? (
        <p>Your Spruce account has successfully been connected to Payaca.</p>
      ) : (
        <>
          Connect Payaca with Spruce for heat pump design and estimation.
          <ConnectionControlBenefitList
            benefits={['Push Projects to Spruce for heat loss calculations.']}
          />
        </>
      )}
      <Modal
        isOpen={isSettingUpConnection}
        onClose={() => setIsSettingUpConnection(false)}
        title="Connect your Spruce account"
      >
        <SpruceConnectionForm
          onSuccess={() => setIsSettingUpConnection(false)}
        />
      </Modal>
    </ConnectionControl>
  );
};

const SpruceConnectionForm: FC<{ onSuccess: () => void }> = (props) => {
  const { onSuccess } = props;
  const { mutateAsync: connectAccountToSpruce } = useConnectAccountToSpruce();
  const { pushSuccess } = useToastContext();
  const [serverError, setServerError] = useState<string | null>(null);

  const formMethods = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormSchema) => {
    setServerError(null);
    const response = await connectAccountToSpruce(data);
    switch (response.connectAccountToSpruce.__typename) {
      case 'ConnectAccountToSpruceSuccess': {
        pushSuccess('Your Spruce account has been successfully connected.');
        return onSuccess();
      }
      case 'ConnectAccountToSpruceInvalidAPIKey': {
        return setServerError(
          'The API key you entered appears to be invalid. Please check your Spruce account and try again.'
        );
      }
      default: {
        return setServerError(
          'An unknown error occurred, please try again later.'
        );
      }
    }
  };

  return (
    <Form methods={formMethods} onSubmit={onSubmit}>
      <Modal.Body>
        <p className="mb-4">
          Please enter the API key from your Spruce account. You can find this
          in your Spruce settings under API access.
        </p>
        <Field name="apiKey">
          <Field.Label>API key</Field.Label>
          <RawInput
            {...formMethods.register('apiKey')}
            placeholder="Your Spruce API key"
            autoComplete="off"
          />
        </Field>
        {serverError && (
          <p className="mt-2 text-sm text-red-600">{serverError}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            type="submit"
            isProcessing={formMethods.formState.isSubmitting}
          >
            Connect
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};
